import { ALL_KNOWN_MODULE_IDS } from '../constants/knownModuleIds'
import {
  CONFIGURACION_EQUIPO_MODULE_ID,
  defaultConfiguracionEquipoAccess,
} from '../constants/configuracionEquipoModule'
import {
  CONFIGURACION_API_KEY_MODULE_ID,
  defaultConfiguracionApiKeyAccess,
} from '../constants/configuracionApiKeyModule'
import {
  FICHA_CLIENTE_MODULE_ID,
  defaultFichaClienteAccess,
} from '../constants/fichaClienteModule'
import {
  AVANCE_CONTABLE_MODULE_ID,
  defaultAvanceContableAccess,
} from '../constants/avanceContableModule'
import {
  CONFIGURACION_AUTOMATIZACIONES_MODULE_ID,
  defaultConfiguracionAutomatizacionesAccess,
} from '../constants/configuracionAutomatizacionesModule'
import {
  MULTAS_SII_MODULE_ID,
  defaultMultasSiiAccess,
} from '../constants/multasSiiModule'
import {
  isRepositorioFlujoCajaModuleId,
  defaultRepositorioFlujoCajaCompanyAccess,
  defaultRepositorioFlujoCajaUserAdminAccess,
} from '../constants/repositorioFlujoCajaModule'
import {
  isRepositorioHaberesVariablesModuleId,
  defaultRepositorioHaberesVariablesCompanyAccess,
  defaultRepositorioHaberesVariablesUserAdminAccess,
} from '../constants/repositorioHaberesVariablesModule'
import {
  STAFF_DEFAULT_MODULE_IDS,
  defaultStaffModuleAccess,
} from '../constants/f29Modules'
import { isRrhhModule } from '../constants/rrhhModules'
import {
  isExplicitGrantModule,
  defaultExplicitGrantModuleAccess,
} from '../constants/explicitGrantModules'
import { isModuleGrantModule } from '../constants/moduleGrantFeatures'
import {
  isCompanyGrantModule,
  defaultCompanyGrantModuleAccess,
} from '../constants/companyGrantModules'

/**
 * Sin fila en modulePermissions → defaultWhenNoRow (UI admin: true), salvo:
 * - configuracion_equipo / configuracion_api_key / configuracion_automatizaciones / ficha_cliente (usuario) / avance_contable (usuario) / multas_sii / F29
 * - ficha_cliente y avance_contable en empresa: sin fila quedan habilitados (defaultWhenNoRow)
 * - repositorio_flujo_caja_* / repositorio_haberes_variables (empresa opt-in; usuario: solo grant explícito en admin)
 * - STAFF_DEFAULT_MODULE_IDS / RRHH + roles: ADMIN/COLABORADOR/SUPER_COLABORADOR ON; USER_SMARTCFO OFF
 * - mismos STAFF_DEFAULT sin roles (empresa): OFF sin fila
 * - Contabilidad en MODULE_IDS_REQUIRE_EXPLICIT_GRANT: OFF sin fila
 * - MODULE_IDS_REQUIRE_COMPANY_GRANT (opt-in por empresa: empresa false, usuario true)
 * - MODULE_IDS_REQUIRE_EXPLICIT_GRANT (empresa opt-in → false si no hay fila)
 */
export function resolveModuleHasAccess(
  existingPerm,
  moduleId,
  defaultWhenNoRow = true,
  roles,
  allModulePermissions,
) {
  if (moduleId === CONFIGURACION_EQUIPO_MODULE_ID) {
    if (!existingPerm) return defaultConfiguracionEquipoAccess(roles)
    const v = existingPerm.hasAccess
    if (v === true || v === false) return v
    return defaultConfiguracionEquipoAccess(roles)
  }
  if (moduleId === CONFIGURACION_API_KEY_MODULE_ID) {
    if (!existingPerm) return defaultConfiguracionApiKeyAccess(roles)
    const v = existingPerm.hasAccess
    if (v === true || v === false) return v
    return defaultConfiguracionApiKeyAccess(roles)
  }
  // Usuario: solo ADMIN si no hay fila. Empresa (sin roles): cae al default true de más abajo.
  if (moduleId === FICHA_CLIENTE_MODULE_ID && roles) {
    if (!existingPerm) return defaultFichaClienteAccess(roles)
    const v = existingPerm.hasAccess
    if (v === true || v === false) return v
    return defaultFichaClienteAccess(roles)
  }
  // Usuario: ADMIN, COLABORADOR y SUPER_COLABORADOR si no hay fila. Empresa: default true.
  if (moduleId === AVANCE_CONTABLE_MODULE_ID && roles) {
    if (!existingPerm) return defaultAvanceContableAccess(roles)
    const v = existingPerm.hasAccess
    if (v === true || v === false) return v
    return defaultAvanceContableAccess(roles)
  }
  if (moduleId === CONFIGURACION_AUTOMATIZACIONES_MODULE_ID) {
    if (!existingPerm) return defaultConfiguracionAutomatizacionesAccess(roles)
    const v = existingPerm.hasAccess
    if (v === true || v === false) return v
    return defaultConfiguracionAutomatizacionesAccess(roles)
  }
  if (moduleId === MULTAS_SII_MODULE_ID && roles) {
    if (!existingPerm) return defaultMultasSiiAccess(roles)
    const v = existingPerm.hasAccess
    if (v === true || v === false) return v
    return defaultMultasSiiAccess(roles)
  }
  if (isRepositorioFlujoCajaModuleId(moduleId)) {
    if (roles) {
      return defaultRepositorioFlujoCajaUserAdminAccess(existingPerm)
    }
    return defaultRepositorioFlujoCajaCompanyAccess(
      existingPerm,
      allModulePermissions,
      moduleId,
    )
  }
  if (isRepositorioHaberesVariablesModuleId(moduleId)) {
    if (roles) {
      return defaultRepositorioHaberesVariablesUserAdminAccess(existingPerm)
    }
    return defaultRepositorioHaberesVariablesCompanyAccess(existingPerm)
  }
  if (STAFF_DEFAULT_MODULE_IDS.includes(moduleId) || isRrhhModule(moduleId)) {
    if (roles) {
      if (!existingPerm) return defaultStaffModuleAccess(roles)
      const v = existingPerm.hasAccess
      if (v === true || v === false) return v
      return defaultStaffModuleAccess(roles)
    }
    // Modal empresa: sin fila → OFF (opt-in). Staff sigue entrando por default de rol en el usuario.
    return defaultExplicitGrantModuleAccess(existingPerm)
  }
  if (isModuleGrantModule(moduleId) && roles) {
    if (!existingPerm) return defaultStaffModuleAccess(roles)
    const v = existingPerm.hasAccess
    if (v === true || v === false) return v
    return defaultStaffModuleAccess(roles)
  }
  if (isCompanyGrantModule(moduleId)) {
    // `roles` solo llega al pintar un usuario: ahí la fila ausente se hereda.
    return defaultCompanyGrantModuleAccess(existingPerm, Boolean(roles))
  }
  if (isExplicitGrantModule(moduleId)) {
    return defaultExplicitGrantModuleAccess(existingPerm)
  }
  if (!existingPerm) return defaultWhenNoRow
  const v = existingPerm.hasAccess
  if (v === true || v === false) return v
  return ALL_KNOWN_MODULE_IDS.includes(moduleId)
}
